<template lang="html">
	<form class="omapi-settings-page" @submit.prevent="saveChanges">
		<core-save-button
			v-if="!isLoading"
			size="large"
			color="green"
			class="omapi-settings-page__top-save"
			:saving="isSaving"
			:disabled="!changed"
		/>

		<slot name="tabs"></slot>

		<common-alerts id="om-settings-errors" :alerts="alerts" />

		<settings-not-connected v-if="!connected" />

		<common-card title="Miscellaneous Settings">
			<core-loading v-if="isLoading"></core-loading>
			<div v-else>
				<div v-if="canAutoUpdate" class="omapi-card-setting-section">
					<header>
						<h3>Automatic Updates</h3>
					</header>
					<div>
						<common-selectable-group
							group-name="auto_updates"
							:options="[
								{
									label: 'Yes (recommended)',
									value: 'all',
									// eslint-disable-next-line max-len
									help: 'Get the latest features, bug fixes, and security updates as they are released',
									checked: 'all' === auto_updates,
								},
								{
									label: 'Minor Only',
									value: 'minor',
									help: 'Get bug fixes and security updates, but not major features',
									checked: 'minor' === auto_updates,
								},
								{
									label: 'None',
									value: '',
									help: 'Manually update everything',
									checked: !auto_updates,
								},
							]"
							@input="setValue"
						/>
					</div>
				</div>

				<div v-if="connected && apiKeyValid" class="omapi-card-setting-section">
					<header>
						<h3>Enable Revenue Attribution?</h3>
					</header>
					<div>
						<p>Would you like to see how much revenue your OptinMonster campaigns generate on your site?</p>
						<div class="row-revenue-attribution-buttons">
							<core-alert :type="alertType" :icon="alertType" class="row-revenue-attribution-status">
								<div class="alert-message">
									{{ revenueAttributionEnabled ? 'Enabled' : 'Disabled' }}
								</div>
							</core-alert>
							<p>
								<a :href="$urls.app('account/#advanced-settings')" rel="noopener" target="_blank"
									>Click here to manage it in your account</a
								>
								or
								<a
									:href="
										$urls.docs(
											// eslint-disable-next-line max-len
											'revenue-attribution/?utm_source=WordPress&utm_medium=MiscSettings&utm_campaign=Plugin'
										)
									"
									class="omapi-link-arrow-after"
									rel="noopener"
									target="_blank"
									>Learn more</a
								>
							</p>
						</div>
					</div>
				</div>

				<div class="omapi-card-setting-section">
					<header>
						<h3>Hide Announcements?</h3>
					</header>
					<div>
						<p>Would you like to hide plugin announcements and update details?</p>

						<common-toggle-input v-model="hide_announcements" on-text="Yes" off-text="No" />
					</div>
				</div>

				<div class="omapi-card-setting-section">
					<header>
						<h3>Enable Anonymous Usage Tracking?</h3>
					</header>
					<div>
						<p>
							By allowing us to track usage data we can better help you because we know which WordPress
							configurations, themes and plugins we should test.
						</p>

						<common-toggle-input v-model="usage_tracking" on-text="Yes" off-text="No" />
					</div>
				</div>

				<div class="omapi-card-setting-section">
					<header></header>
					<div>
						<core-save-button size="large" color="green" :saving="isSaving" :disabled="!changed" />
					</div>
				</div>
			</div>
		</common-card>
	</form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { defaultState } from '@/settings/store/state';
import { bulkWatchHandler } from '@/utils';

const defaults = defaultState()['miscSettings'];
const settingKeys = Object.keys(defaults);

export default {
	data() {
		return {
			...this.getSettings(defaults),
			changed: false,
			orig: false,
		};
	},
	computed: {
		...mapState(['error', 'alerts', 'apiKeyValid']),
		...mapGetters('settings', ['miscSettings']),
		...mapGetters(['connected', 'userAttribute']),
		isLoading() {
			return this.$store.getters.isFetching('miscSettings') && !this.$store.getters.isFetched('miscSettings');
		},
		isSaving() {
			return this.$store.getters.isLoading('miscSettingsSave');
		},
		canAutoUpdate() {
			// TODO: Determine how this will work functionally.
			return false;
		},
		revenueAttributionEnabled() {
			return this.userAttribute('revenueAttribution.enabled');
		},
		alertType() {
			return this.revenueAttributionEnabled ? 'success' : 'warn';
		},
	},
	watch: {
		...bulkWatchHandler(settingKeys, 'checkIfChanged'),
		miscSettings(newValue, oldVal) {
			if (newValue !== oldVal) {
				Object.keys(newValue).forEach((k) => {
					this[k] = newValue[k];
				});
			}
		},
	},
	beforeMount() {
		this.fetch();
	},
	methods: {
		...mapActions('settings', ['fetchMiscSettings', 'saveMiscSettings']),
		saveChanges() {
			this.saveMiscSettings(this.getSettings(this)).then(() => this.resetOrig());
		},
		setValue(event) {
			let name = event.target.name;
			let value = '';

			switch (event.target.type) {
				case 'checkbox':
					// For toggles, 'checked' is 'off'
					value = !event.target.checked;
					break;
				case 'radio':
					value = event.target.value;
					break;
			}

			this[name] = value;
		},
		fetch() {
			if (!this.isLoading) {
				this.fetchMiscSettings().then(() => this.resetOrig());
			}
		},
		resetOrig() {
			this.changed = false;
			this.orig = { ...this.miscSettings };
		},
		checkIfChanged() {
			this.changed = Boolean(settingKeys.find((k) => this[k] !== this.$get(`orig.${k}`)));
		},
		getSettings(allSettings) {
			const all = {};
			settingKeys.forEach((k) => {
				all[k] = allSettings[k];
			});

			return all;
		},
	},
};
</script>
<style lang="scss" scoped>
.omapi-settings-page {
	position: relative;
	.omapi-settings-page__top-save {
		position: absolute;
		top: 12px;
		right: 0;
		margin-right: 0;
	}
}
.row-revenue-attribution-buttons {
	display: flex;
	align-items: center;

	.row-revenue-attribution-status.omapi-alert {
		padding: 6px 20px 5px;
		margin-right: 18px;
		margin-bottom: 0;

		.alert-message {
			font-weight: 600;
		}
	}

	p {
		margin: 0;
	}
}
</style>
